import { FRAMEWORK_STUDIO_LABELS } from './frameworkStudioLabels';

/**
 * Helpers for the Rights Management page.
 *
 * Permissions are stored per **role** (not per user). Keys use **menuId** so
 * API menu labels cannot break edit/save matching.
 */

// ---------------------------------------------------------------------------
// Types — API shapes (camelCase from .NET JSON)
// ---------------------------------------------------------------------------

export interface PermissionType {
  id: number;
  permissionCode: string;
  permissionName: string;
}

export interface MenuOption {
  id: number;
  name: string;
  menuName?: string;
  parentMenuId?: number | null;
  displayOrder?: number | null;
}

const normalizeMenuLabelKey = (menuName?: string | null, displayName?: string | null): string => {
  const raw = (displayName || menuName || '').trim().toLowerCase();
  return raw.replace(/_/g, ' ').replace(/\s+/g, ' ');
};

/** API seed labels → product labels (routes / menu keys unchanged). */
const MENU_DISPLAY_LABEL_OVERRIDES: Record<string, string> = {
  'assessment blueprints': FRAMEWORK_STUDIO_LABELS.assessmentAssignments,
  'assessment blueprint': FRAMEWORK_STUDIO_LABELS.assessmentAssignments,
  assessmentblueprints: FRAMEWORK_STUDIO_LABELS.assessmentAssignments,
};

/** Labels from API seed (DisplayName / MenuName). */
export const resolveMenuDisplayLabel = (
  menuName?: string | null,
  displayName?: string | null
): string => {
  const raw = displayName || menuName || '';
  const override = MENU_DISPLAY_LABEL_OVERRIDES[normalizeMenuLabelKey(menuName, displayName)];
  return override ?? raw;
};

export interface ApiMenuPermission {
  id: number;
  permissionName?: string;
  isAllowed: number;
}

export interface ApiMenuNode {
  id?: number;
  displayName?: string;
  name?: string;
  menuName?: string;
  isCategory?: boolean;
  permissions?: ApiMenuPermission[];
  subMenus?: ApiMenuNode[];
}

/** One role and its menu/permission tree from `GET /Menu/AllUsersRolesPermissions`. */
export interface ApiRolePermissions {
  roleId: number;
  roleName?: string;
  menus?: ApiMenuNode[];
}

export interface RoleOption {
  id: number;
  roleName: string;
}

// ---------------------------------------------------------------------------
// Types — UI shapes
// ---------------------------------------------------------------------------

/**
 * Flat permission map used by the create/edit form.
 * Key: menuId → permission code (lowercase) → enabled.
 */
export type PermissionsByMenuId = Record<number, Record<string, boolean>>;

/** One row in the rights management table. */
export interface RoleRightsRow {
  roleId: number;
  roleName: string;
  permissions: PermissionsByMenuId;
  assignedMenuNames: string[];
}

export interface RoleRightsFormState {
  /** Set when editing an existing role; `null` means create mode. */
  editingRoleId: number | null;
  roleId: string;
  permissions: PermissionsByMenuId;
}

export const EMPTY_ROLE_RIGHTS_FORM: RoleRightsFormState = {
  editingRoleId: null,
  roleId: '',
  permissions: {},
};

// ---------------------------------------------------------------------------
// API → UI
// ---------------------------------------------------------------------------

/** Resolves a readable label for a menu node from the API tree. */
const getMenuDisplayName = (menu: ApiMenuNode): string =>
  resolveMenuDisplayLabel(
    menu.menuName || menu.name,
    menu.displayName || menu.name || menu.menuName
  );

const isAssignableMenuId = (menuId: number | undefined): menuId is number =>
  typeof menuId === 'number' && Number.isFinite(menuId) && menuId > 0;

/**
 * Walks the menu tree and builds a flat map of allowed permissions
 * keyed by real menu id and permission code.
 */
export const buildPermissionsByMenuId = (
  menus: ApiMenuNode[] | undefined,
  permissionTypes: PermissionType[]
): PermissionsByMenuId => {
  const permissionsByMenu: PermissionsByMenuId = {};

  const visitMenu = (menu: ApiMenuNode) => {
    // Synthetic category / virtual folders are not assignable targets
    if (menu.isCategory || !isAssignableMenuId(menu.id)) {
      (menu.subMenus || []).forEach(visitMenu);
      return;
    }

    const menuId = menu.id;

    (menu.permissions || []).forEach((permission) => {
      if (permission.isAllowed !== 1) {
        return;
      }

      const permissionType = permissionTypes.find((type) => type.id === permission.id);
      const actionCode = permissionType
        ? permissionType.permissionCode.toLowerCase()
        : (permission.permissionName || '').toLowerCase();

      if (!actionCode) {
        return;
      }

      permissionsByMenu[menuId] = permissionsByMenu[menuId] || {};
      permissionsByMenu[menuId][actionCode] = true;
    });

    (menu.subMenus || []).forEach(visitMenu);
  };

  (menus || []).forEach(visitMenu);
  return permissionsByMenu;
};

const resolveAssignedMenuNames = (
  permissions: PermissionsByMenuId,
  roleMenus: ApiMenuNode[] | undefined,
  menuOptions: MenuOption[]
): string[] => {
  const namesById = new Map<number, string>();

  const visit = (menu: ApiMenuNode) => {
    if (isAssignableMenuId(menu.id) && !menu.isCategory) {
      const label = getMenuDisplayName(menu);
      if (label) {
        namesById.set(menu.id, label);
      }
    }
    (menu.subMenus || []).forEach(visit);
  };
  (roleMenus || []).forEach(visit);

  menuOptions.forEach((menu) => {
    const label = resolveMenuDisplayLabel(menu.menuName, menu.name);
    if (!namesById.has(menu.id) && label) {
      namesById.set(menu.id, label);
    }
  });

  return Object.keys(permissions)
    .map((id) => Number(id))
    .filter((id) => Object.values(permissions[id] || {}).some(Boolean))
    .map((id) => namesById.get(id) || `Menu #${id}`)
    .sort((a, b) => a.localeCompare(b));
};

/**
 * Converts a single API role-permissions object into a table row.
 */
export const mapApiRoleToTableRow = (
  apiRole: ApiRolePermissions,
  roleOptions: RoleOption[],
  permissionTypes: PermissionType[],
  menuOptions: MenuOption[] = []
): RoleRightsRow => {
  const permissions = buildPermissionsByMenuId(apiRole.menus, permissionTypes);
  const matchedRole = roleOptions.find((role) => role.id === apiRole.roleId);

  return {
    roleId: apiRole.roleId,
    roleName: apiRole.roleName || matchedRole?.roleName || 'Unknown Role',
    permissions,
    assignedMenuNames: resolveAssignedMenuNames(permissions, apiRole.menus, menuOptions),
  };
};

/** Converts the full API list into table rows. */
export const mapApiRolesToTableRows = (
  apiRoles: ApiRolePermissions[],
  roleOptions: RoleOption[],
  permissionTypes: PermissionType[],
  menuOptions: MenuOption[] = []
): RoleRightsRow[] =>
  apiRoles.map((apiRole) =>
    mapApiRoleToTableRow(apiRole, roleOptions, permissionTypes, menuOptions)
  );

/** Normalizes API response — endpoint may return an array or a single object. */
export const normalizeApiRoleList = (
  response: ApiRolePermissions | ApiRolePermissions[] | null | undefined
): ApiRolePermissions[] => {
  if (Array.isArray(response)) {
    return response;
  }
  return response ? [response] : [];
};

/** Menus sorted for the assign-permissions accordion. */
export const sortMenuOptionsForForm = (menus: MenuOption[]): MenuOption[] =>
  [...menus].sort((a, b) => {
    const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.name || '').localeCompare(b.name || '');
  });

/** One sidebar-style category block in the rights editor. */
export interface MenuRightsCategory {
  id: string;
  name: string;
  items: MenuOption[];
  /** Nested role groups inside a combined card (e.g. Admins). */
  sections?: MenuRightsSection[];
  size?: 'default' | 'large' | 'compact';
}

export interface MenuRightsSection {
  id: string;
  name: string;
  items: MenuOption[];
  size?: 'default' | 'large' | 'compact';
}

const isRootParentId = (parentMenuId: number | null | undefined): boolean =>
  parentMenuId == null || parentMenuId <= 0;

const toMenuOptionFromNode = (
  node: ApiMenuNode,
  optionsById: Map<number, MenuOption>
): MenuOption | null => {
  if (!isAssignableMenuId(node.id) || node.isCategory) {
    return null;
  }

  const existing = optionsById.get(node.id);
  if (existing) {
    return existing;
  }

  return {
    id: node.id,
    name: getMenuDisplayName(node) || `Menu #${node.id}`,
    menuName: node.menuName || node.name,
    parentMenuId: null,
    displayOrder: null,
  };
};

const collectAssignableLeaves = (
  nodes: ApiMenuNode[] | undefined,
  optionsById: Map<number, MenuOption>,
  usedIds: Set<number>
): MenuOption[] => {
  const items: MenuOption[] = [];

  (nodes || []).forEach((node) => {
    const children = node.subMenus || [];
    if (node.isCategory || children.length > 0) {
      items.push(...collectAssignableLeaves(children, optionsById, usedIds));
      return;
    }

    const option = toMenuOptionFromNode(node, optionsById);
    if (!option || usedIds.has(option.id)) {
      return;
    }

    usedIds.add(option.id);
    items.push(option);
  });

  return items;
};

/**
 * Groups flat menus by parent hierarchy when no API tree is available:
 * parents become category cards; leaf menus become rows inside them.
 */
export const groupMenusForRightsForm = (menus: MenuOption[]): MenuRightsCategory[] => {
  const byId = new Map(menus.map((menu) => [menu.id, menu]));
  const childrenByParent = new Map<number, MenuOption[]>();

  menus.forEach((menu) => {
    const parentId = menu.parentMenuId;
    if (parentId == null || parentId <= 0 || !byId.has(parentId)) {
      return;
    }
    const siblings = childrenByParent.get(parentId) || [];
    siblings.push(menu);
    childrenByParent.set(parentId, siblings);
  });

  const collectLeaves = (menu: MenuOption): MenuOption[] => {
    const children = childrenByParent.get(menu.id) || [];
    if (children.length === 0) {
      return [menu];
    }
    return children.flatMap(collectLeaves);
  };

  const roots = menus.filter((menu) => {
    const parentId = menu.parentMenuId;
    return isRootParentId(parentId) || !byId.has(parentId as number);
  });

  const categories: MenuRightsCategory[] = [];
  const standalone: MenuOption[] = [];

  sortMenuOptionsForForm(roots).forEach((root) => {
    const children = childrenByParent.get(root.id) || [];
    if (children.length === 0) {
      standalone.push(root);
      return;
    }

    const items = sortMenuOptionsForForm(collectLeaves(root));
    if (items.length === 0) {
      return;
    }

    categories.push({
      id: `cat-${root.id}`,
      name: root.name || `Menu #${root.id}`,
      items,
    });
  });

  if (standalone.length > 0) {
    categories.push({
      id: 'cat-general',
      name: 'General',
      items: sortMenuOptionsForForm(standalone),
    });
  }

  return categories;
};

/**
 * Builds category cards from the role/menu tree (same grouping as the sidebar).
 * Any flat menus missing from the tree land in a final General group.
 */
export const groupMenusFromApiTree = (
  tree: ApiMenuNode[] | undefined,
  menuOptions: MenuOption[]
): MenuRightsCategory[] => {
  if (!tree || tree.length === 0) {
    return groupMenusForRightsForm(menuOptions);
  }

  const optionsById = new Map(menuOptions.map((menu) => [menu.id, menu]));
  const usedIds = new Set<number>();
  const categories: MenuRightsCategory[] = [];
  const standalone: MenuOption[] = [];

  tree.forEach((node, index) => {
    const children = node.subMenus || [];
    const label = getMenuDisplayName(node) || `Group ${index + 1}`;

    if (node.isCategory || children.length > 0) {
      const items = sortMenuOptionsForForm(
        collectAssignableLeaves(children, optionsById, usedIds)
      );
      if (items.length > 0) {
        categories.push({
          id: `tree-${node.id ?? index}-${label}`,
          name: label,
          items,
        });
      }
      return;
    }

    const option = toMenuOptionFromNode(node, optionsById);
    if (option && !usedIds.has(option.id)) {
      usedIds.add(option.id);
      standalone.push(option);
    }
  });

  menuOptions.forEach((menu) => {
    if (!usedIds.has(menu.id)) {
      standalone.push(menu);
    }
  });

  if (standalone.length > 0) {
    categories.push({
      id: 'cat-general',
      name: 'General',
      items: sortMenuOptionsForForm(standalone),
    });
  }

  return categories;
};

/** Stable product order for non-admin platform roles. */
const ROLE_CATEGORY_ORDER = ['client', 'superuser', 'sme'];

const ADMIN_ROLE_KEYS = new Set(['superadmin', 'admin']);

/** Short shared labels that need the role name to stay understandable. */
const ROLE_AMBIGUOUS_MENU_LABEL =
  /^(dashboard|assessment|assessments|reports?|chats?|profile|projects?|team members?|capabilities?)$/i;

const normalizeRoleKey = (roleName: string): string =>
  roleName.trim().toLowerCase().replace(/\s+/g, '');

const isAdminRoleKey = (roleName: string): boolean =>
  ADMIN_ROLE_KEYS.has(normalizeRoleKey(roleName));

const roleSortIndex = (roleName: string): number => {
  const index = ROLE_CATEGORY_ORDER.indexOf(normalizeRoleKey(roleName));
  return index === -1 ? ROLE_CATEGORY_ORDER.length + 1 : index;
};

const resolveRoleName = (
  role: ApiRolePermissions,
  roleNameById: Map<number, string>
): string => role.roleName || roleNameById.get(role.roleId) || `Role #${role.roleId}`;

/** e.g. Dashboard → "Client Dashboard" when the bare name is ambiguous. */
export const disambiguateMenuLabelForRole = (
  menuName: string,
  roleName: string
): string => {
  const label = (menuName || '').trim();
  const role = (roleName || '').trim();
  if (!label) {
    return role ? `${role} menu` : 'Menu';
  }
  if (!role || !ROLE_AMBIGUOUS_MENU_LABEL.test(label)) {
    return label;
  }
  if (label.toLowerCase().startsWith(role.toLowerCase())) {
    return label;
  }
  return `${role} ${label}`;
};

const buildRoleMenuItems = (
  role: ApiRolePermissions,
  roleName: string,
  optionsById: Map<number, MenuOption>,
  usedIds: Set<number>
): MenuOption[] => {
  const roleUsed = new Set<number>();
  const leaves = collectAssignableLeaves(role.menus, optionsById, roleUsed);

  return sortMenuOptionsForForm(
    leaves
      .filter((menu) => !usedIds.has(menu.id))
      .map((menu) => {
        usedIds.add(menu.id);
        return {
          ...menu,
          name: disambiguateMenuLabelForRole(menu.name, roleName),
        };
      })
  );
};

/**
 * Role-grouped cards for the rights editor.
 * Admin + SuperAdmin are merged into one top "Admins" card (SuperAdmin first /
 * larger so its dashboard is kept and labeled). Other roles follow.
 */
export const groupMenusByRole = (
  apiRoles: ApiRolePermissions[],
  menuOptions: MenuOption[],
  roleOptions: RoleOption[] = []
): MenuRightsCategory[] => {
  if (!apiRoles.length) {
    return groupMenusForRightsForm(menuOptions);
  }

  const optionsById = new Map(menuOptions.map((menu) => [menu.id, menu]));
  const usedIds = new Set<number>();
  const roleNameById = new Map(roleOptions.map((role) => [role.id, role.roleName]));

  const adminRoles: ApiRolePermissions[] = [];
  const otherRoles: ApiRolePermissions[] = [];

  apiRoles.forEach((role) => {
    const roleName = resolveRoleName(role, roleNameById);
    if (isAdminRoleKey(roleName)) {
      adminRoles.push(role);
    } else {
      otherRoles.push(role);
    }
  });

  // SuperAdmin before Admin so shared dashboards stay under SuperAdmin.
  adminRoles.sort((a, b) => {
    const keyA = normalizeRoleKey(resolveRoleName(a, roleNameById));
    const keyB = normalizeRoleKey(resolveRoleName(b, roleNameById));
    if (keyA === keyB) {
      return 0;
    }
    if (keyA === 'superadmin') {
      return -1;
    }
    if (keyB === 'superadmin') {
      return 1;
    }
    return keyA.localeCompare(keyB);
  });

  otherRoles.sort((a, b) => {
    const nameA = resolveRoleName(a, roleNameById);
    const nameB = resolveRoleName(b, roleNameById);
    const order = roleSortIndex(nameA) - roleSortIndex(nameB);
    if (order !== 0) {
      return order;
    }
    return nameA.localeCompare(nameB);
  });

  const categories: MenuRightsCategory[] = [];

  if (adminRoles.length > 0) {
    const sections: MenuRightsSection[] = adminRoles.map((role) => {
      const roleName = resolveRoleName(role, roleNameById);
      const roleKey = normalizeRoleKey(roleName);
      const items = buildRoleMenuItems(role, roleName, optionsById, usedIds);

      return {
        id: `role-${role.roleId}`,
        name: roleName,
        items,
        size: roleKey === 'superadmin' ? 'large' : 'compact',
      };
    }).filter((section) => section.items.length > 0);

    if (sections.length > 0) {
      categories.push({
        id: 'cat-admins',
        name: 'Admins',
        items: [],
        sections,
        size: 'large',
      });
    }
  }

  otherRoles.forEach((role) => {
    const roleName = resolveRoleName(role, roleNameById);
    const items = buildRoleMenuItems(role, roleName, optionsById, usedIds);

    if (items.length === 0) {
      return;
    }

    categories.push({
      id: `role-${role.roleId}`,
      name: roleName,
      items,
    });
  });

  const leftovers = sortMenuOptionsForForm(
    menuOptions.filter((menu) => !usedIds.has(menu.id))
  );
  if (leftovers.length > 0) {
    categories.push({
      id: 'cat-general',
      name: 'Other',
      items: leftovers,
    });
  }

  return categories;
};

/** Prefers the richest role menu tree so category cards mirror the sidebar. */
export const pickCanonicalMenuTree = (
  apiRoles: ApiRolePermissions[]
): ApiMenuNode[] => {
  let best: ApiMenuNode[] = [];
  let bestScore = -1;

  const scoreTree = (nodes: ApiMenuNode[] | undefined): number => {
    let count = 0;
    const visit = (node: ApiMenuNode) => {
      count += 1;
      (node.subMenus || []).forEach(visit);
    };
    (nodes || []).forEach(visit);
    return count;
  };

  apiRoles.forEach((role) => {
    const score = scoreTree(role.menus);
    if (score > bestScore) {
      bestScore = score;
      best = role.menus || [];
    }
  });

  return best;
};

/** Count of enabled permission actions for one menu in the form. */
export const countEnabledPermissions = (
  permissions: PermissionsByMenuId,
  menuId: number
): number => Object.values(permissions[menuId] || {}).filter(Boolean).length;

// ---------------------------------------------------------------------------
// UI → API
// ---------------------------------------------------------------------------

/**
 * Builds the payload for `POST /Menu/AddOrUpdateUserMenuPermission`.
 * Only enabled permissions are included — the API revokes omitted entries.
 */
export const buildRolePermissionsPayload = (
  menuOptions: MenuOption[],
  permissionTypes: PermissionType[],
  formPermissions: PermissionsByMenuId
): Array<{ menuId: number; permissionId: number; isAllowed: 0 | 1 }> => {
  const details: Array<{ menuId: number; permissionId: number; isAllowed: 0 | 1 }> = [];

  menuOptions.forEach((menu) => {
    if (!isAssignableMenuId(menu.id)) {
      return;
    }

    const selectedForMenu = formPermissions[menu.id] || {};

    permissionTypes.forEach((permissionType) => {
      const code = permissionType.permissionCode.toLowerCase();
      if (selectedForMenu[code]) {
        details.push({
          menuId: menu.id,
          permissionId: permissionType.id,
          isAllowed: 1,
        });
      }
    });
  });

  return details;
};

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------

/** Roles that do not yet appear in the rights table (for the create dropdown). */
export const getRolesAvailableForCreate = (
  roleOptions: RoleOption[],
  existingRows: RoleRightsRow[]
): RoleOption[] => {
  const configuredRoleIds = new Set(existingRows.map((row) => row.roleId));
  return roleOptions.filter((role) => !configuredRoleIds.has(role.id));
};
